import type { Account, AssetVisibility, Capability, DeprecatedData, Permission, Plan, ProductEnvironment, User, ThemeName } from '@cld/unicorn-api';
import type { ProductName, StyledComponentsThemeObject } from './types';

export type ApplicationStartupState = 'detectingActiveProduct' | 'pickingInitialProductEnvironment' | 'fetchingInitialData';
type FetchState<RequestData> = { status: 'idle' } | { status: 'pending'; requestData: RequestData };
type EmptyRequestData = Record<string, never>;

// FIXME: with the advanced reload capabilities we have, we could add a side effect
// to periodically (eg. every 30-60 minutes) reload data in background preventing displaying stale content
// in cases where user does not refresh the page for extended periods of time
export type State =
  | {
      applicationStatus: 'startup';
      startupStep:
        | { name: 'detectingActiveProduct' }
        | { name: 'pickingInitialProductEnvironment'; activeProductName: ProductName }
        | { name: 'fetchingInitialData'; activeProductName: ProductName; productEnvironment: ProductEnvironment };
    }
  | {
      applicationStatus: 'startupError';
      error: { message: string; stack: string; data?: Record<string, unknown> };
    }
  | {
      applicationStatus: 'ready';
      account: Account;
      capabilities: Capability[];
      plan: Plan;
      permissions: Permission[];
      productEnvironment: ProductEnvironment;
      user: Omit<User, 'impersonationActive'>;
      deprecatedData: DeprecatedData;
      assetVisibility: AssetVisibility;
      theme: { name: ThemeName; styledComponentsThemeObject: StyledComponentsThemeObject };
      activeProductName: ProductName;
      isImpersonationActive: boolean;
      mainLogoUrl: string;

      productEnvironmentChangeState: { status: 'idle' } | { status: 'pending'; newExternalId: ProductEnvironment['externalId'] };
      productEnvironmentFetchState: FetchState<{ externalId: ProductEnvironment['externalId'] }>;
      accountFetchState: FetchState<EmptyRequestData>;
      planFetchState: FetchState<EmptyRequestData>;
      userFetchState: FetchState<EmptyRequestData>;
      capabilitiesFetchState: FetchState<EmptyRequestData>;
      deprecatedDataFetchState: FetchState<EmptyRequestData>;
      permissionsFetchState: FetchState<EmptyRequestData>;
      postGdprLogState: FetchState<{ details: string; justification: string }>;
    };

export const initialState: State = {
  applicationStatus: 'startup',
  startupStep: { name: 'detectingActiveProduct' },
};
